import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listAllSubscriptions, formatCAD, daysUntil, type AdminSubscriptionRow } from '@/lib/adminBilling';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

const STATUSES = ['all','trial','pilot','active','past_due','canceled'] as const;
type Filter = typeof STATUSES[number];

export default function SubscriptionsList() {
  const [params, setParams] = useSearchParams();
  const initial = (params.get('status') as Filter) ?? 'all';
  const [filter, setFilter] = useState<Filter>(STATUSES.includes(initial) ? initial : 'all');
  const [rows, setRows] = useState<AdminSubscriptionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listAllSubscriptions()
      .then((r) => { if (!cancelled) { setRows(r); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(humanize(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  function changeFilter(next: Filter) {
    setFilter(next);
    if (next === 'all') params.delete('status'); else params.set('status', next);
    setParams(params);
  }

  const filtered = useMemo(() => filter === 'all' ? rows : rows.filter((r) => r.status === filter), [rows, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    trial: rows.filter((r) => r.status === 'trial').length,
    pilot: rows.filter((r) => r.status === 'pilot').length,
    active: rows.filter((r) => r.status === 'active').length,
    past_due: rows.filter((r) => r.status === 'past_due').length,
    canceled: rows.filter((r) => r.status === 'canceled').length,
  }), [rows]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading subscriptions…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Subscriptions</h2>
        <p className="text-sm text-muted-foreground">All organizations and their plan state. Filter by status, click into a row to act.</p>
      </header>

      <div className="flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => changeFilter(s)}
            className={`rounded-full border px-3 py-1 text-xs ${
              filter === s
                ? 'border-2'
                : 'border-border hover:bg-muted/40 text-muted-foreground'
            }`}
            style={filter === s ? { borderColor: 'hsl(var(--brand-navy))', color: 'hsl(var(--brand-navy))' } : undefined}
          >
            {s === 'all' ? 'All' : s.replace('_',' ')} ({counts[s]})
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[920px]">
          <thead>
            <tr className="border-b border-border text-left">
              <Th>Organization</Th>
              <Th>Status</Th>
              <Th>Tier</Th>
              <Th>Billing</Th>
              <Th>Days remaining</Th>
              <Th className="text-right">Locations</Th>
              <Th className="text-right">MRR</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const days = r.status === 'trial'
                ? daysUntil(r.trial_ends_at)
                : r.status === 'pilot'
                  ? daysUntil(r.pilot_ends_at)
                  : r.status === 'active'
                    ? daysUntil(r.current_period_end)
                    : null;
              return (
                <tr
                  key={r.organization_id}
                  className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/10'} hover:bg-muted/30`}
                >
                  <Td>
                    <Link to={`/admin/subscriptions/${r.organization_id}`} className="hover:underline" style={{ color: 'hsl(var(--brand-navy))' }}>
                      {r.org_display_name}
                    </Link>
                    <div className="text-xs text-muted-foreground">{r.primary_contact_email ?? '—'} · joined {formatDate(r.org_created_at)}</div>
                  </Td>
                  <Td><StatusChip status={r.status} /></Td>
                  <Td>{r.tier ? r.tier[0].toUpperCase() + r.tier.slice(1) : '—'}</Td>
                  <Td className="text-xs">{r.billing_period ?? '—'}</Td>
                  <Td className="text-xs">{days === null ? '—' : `${days}d`}</Td>
                  <Td className="text-right">{r.active_location_count}</Td>
                  <Td className="text-right">{formatCAD(Number(r.mrr_cad))}</Td>
                  <Td>
                    <Link to={`/admin/subscriptions/${r.organization_id}`} className="text-xs underline">
                      Manage →
                    </Link>
                  </Td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">No subscriptions in this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th scope="col" className={`p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}
function StatusChip({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const tones: Record<string, string> = {
    trial: 'bg-blue-50 text-blue-900 border-blue-200',
    pilot: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    active: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    past_due: 'bg-red-50 text-red-900 border-red-200',
    canceled: 'bg-muted/40 text-muted-foreground border-border',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tones[status] ?? ''}`}>
      {status.replace('_',' ')}
    </span>
  );
}
